import React, { useEffect, useState, useRef } from "react";
import "../css/Spinning.css";
import axios from 'axios';

function WellnessCheckInForm() {
    const debugging_without_calling_API = false;
    const formRef = useRef(null);
    const [animationReady, setAnimationReady] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const contentRef = useRef(null);
    const [showHotelSuggestions, setShowHotelSuggestions] = useState(false);
    const [popularHotels, setPopularHotels] = useState([]);

    // Utility functions for hotel and date
    const getISO8601WithTimezone = (date, time) => {
        const [hours, minutes] = time.split(":");
        const updatedDate = new Date(date);
        updatedDate.setHours(parseInt(hours, 10));
        updatedDate.setMinutes(parseInt(minutes, 10));
        updatedDate.setSeconds(0);
        return updatedDate.toISOString();
    };

    const getCurrentDateTime = () => {
        const now = new Date();
        // 手動調整到 GMT+8
        const gmt8Offset = 8 * 60 * 60 * 1000; // 8 小時的毫秒數
        const gmt8Date = new Date(now.getTime() + gmt8Offset + 120 * 1000);
        // 提取日期和時間部分
        const date = gmt8Date.toISOString().split("T")[0]; // 格式化為 YYYY-MM-DD
        const time = gmt8Date.toISOString().split("T")[1].slice(0, 5); // 格式化為 HH:mm
        return { date, time };
    };

    const { date: currentDate, time: currentTime } = getCurrentDateTime();

    // At the beginning of your component, update the URL parameter function
    const getUrlParameter = (name) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    };

    // When initializing your formData state
    const [formData, setFormData] = useState(() => {
        // Get all parameters from URL if they exist
        const hotelNameFromUrl = getUrlParameter('hotel');
        const checkInDateFromUrl = getUrlParameter('checkin');
        const checkOutDateFromUrl = getUrlParameter('checkout');

        return {
            email: "",
            gender: "",
            nationality: "",
            birthday: "",
            name: "",
            sleepTime: "22:00",
            wakeUpTime: "06:00",
            // Use check-in date from URL if available, otherwise use current date
            checkInDate: checkInDateFromUrl || currentDate,
            checkOutDate: checkOutDateFromUrl || "",
            checkOutTime: "",
            sleepProblem: "",
            sleepSolution: "",
            useAroma: "",
            travelWith: "",
            hotelName: hotelNameFromUrl || "",
            roomNumber: "",
            mobileNumber: "",
            remarks: "",
            wellBeing: "",
            aromaStrength: "",
            safetyConcerns: "",
            wellnessPriority: "",
            visitReason: "",
            form_created_time: new Date().toISOString(),
            // Track if fields were pre-filled from URL
            hotelNamePrefilled: !!hotelNameFromUrl,
            checkInDatePrefilled: !!checkInDateFromUrl,
            checkOutDatePrefilled: !!checkOutDateFromUrl
        };
    });

    // Other states
    const [isChecked, setIsChecked] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [showLoadingPage, setShowLoadingPage] = useState(false);
    const [roomNumber, setRoomNumber] = useState("");
    const [serialNumber, setSerialNumber] = useState(null);

    // Define form steps
    const formSteps = [
        {
            id: "welcome",
            title: "Welcome to Your Aromatherapy Experience",
            required: []
        },
        {
            id: "personal",
            title: "Tell Us About Yourself",
            required: ["name", "email", "gender", "birthday", "nationality", "mobileNumber"]
        },
        {
            id: "stay",
            title: "Your Stay Details",
            required: ["hotelName", "checkInDate", "checkOutDate", "sleepTime", "wakeUpTime"]
        },
        {
            id: "preferences",
            title: "Aromatherapy Preferences",
            required: ["sleepProblem", "aromaStrength", "safetyConcerns"]
        },
        {
            id: "enhance",
            title: "Enhance Your Experience",
            required: []
        },
        {
            id: "review",
            title: "Review & Activate",
            required: []
        }
    ];

    // Update progress when step changes
    useEffect(() => {
        setProgress((currentStep / (formSteps.length - 1)) * 100);

        // Animate content when step changes
        if (contentRef.current) {
            contentRef.current.classList.remove('content-visible');
            setTimeout(() => {
                contentRef.current.classList.add('content-visible');
            }, 50);
        }
    }, [currentStep]);

    // Initialize animation
    useEffect(() => {
        setAnimationReady(true);
    }, []);

    // Form validation functions
    const validateCurrentStep = () => {
        const step = formSteps[currentStep];
        const requiredFields = step.required;
        const newErrors = {};

        requiredFields.forEach(field => {
            if (!formData[field] || formData[field].trim() === '') {
                newErrors[field] = `Please provide your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateForm = () => {
        const newErrors = {};

        // Core validation fields
        const coreFields = [
            "name", "email", "gender", "birthday", "nationality", "mobileNumber",
            "hotelName", "checkInDate", "checkOutDate", "sleepTime", "wakeUpTime",
            "sleepProblem", "aromaStrength", "safetyConcerns"
        ];

        coreFields.forEach(field => {
            if (!formData[field] || formData[field].trim() === '') {
                newErrors[field] = `Please provide your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Navigation functions
    const goToNextStep = () => {
        if (currentStep < formSteps.length - 1) {
            if (validateCurrentStep()) {
                setCurrentStep(currentStep + 1);
                window.scrollTo(0, 0);
            } else {
                // Animate error fields
                Object.keys(errors).forEach(fieldName => {
                    const field = document.querySelector(`[name="${fieldName}"]`);
                    if (field && field.closest('.input-group')) {
                        field.closest('.input-group').classList.add('input-error-shake');
                        setTimeout(() => {
                            field.closest('.input-group').classList.remove('input-error-shake');
                        }, 500);
                    }
                });
            }
        }
    };

    const goToPreviousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    // Event handlers
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "nationality") {
            if (value === "Others") {
                setFormData((prev) => ({ ...prev, nationality: "Others" }));
            } else {
                setFormData((prev) => ({ ...prev, nationality: value }));
            }
        } else {
            // Apply animation to the input's parent element
            if (e.target.closest('.input-group')) {
                e.target.closest('.input-group').classList.add('input-animated');
                setTimeout(() => {
                    e.target.closest('.input-group').classList.remove('input-animated');
                }, 300);
            }
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // Hotel autocomplete suggestions
    const suggestHotels = (query) => {
        if (!query || query.length < 2) return [];

        // Example hotel suggestions - in a real app this could be fetched from an API
        const allHotels = [
            { name: "Grand Hyatt", icon: "🏨" },
            { name: "Four Seasons", icon: "🏨" },
            { name: "Ritz-Carlton", icon: "🏨" },
            { name: "Mandarin Oriental", icon: "🏨" },
            { name: "Shangri-La", icon: "🏨" },
            { name: "Peninsula Hotel", icon: "🏨" },
            { name: "Hilton", icon: "🏨" },
            { name: "Marriott", icon: "🏨" },
            { name: "Waldorf Astoria", icon: "🏨" },
            { name: "Conrad", icon: "🏨" },
        ];

        return allHotels.filter(hotel =>
            hotel.name.toLowerCase().includes(query.toLowerCase())
        );
    };

    // Calculate stay duration (in nights)
    const calculateStayDuration = () => {
        if (!formData.checkInDate || !formData.checkOutDate) return 0;

        const checkIn = new Date(formData.checkInDate);
        const checkOut = new Date(formData.checkOutDate);
        const diffTime = checkOut - checkIn;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
    };

    // Calculate sleep duration (in hours)
    const calculateSleepDuration = () => {
        if (!formData.sleepTime || !formData.wakeUpTime) return 0;

        const [sleepHour, sleepMin] = formData.sleepTime.split(':').map(Number);
        const [wakeHour, wakeMin] = formData.wakeUpTime.split(':').map(Number);

        let sleepMinutes = sleepHour * 60 + sleepMin;
        let wakeMinutes = wakeHour * 60 + wakeMin;

        // Handle overnight sleep
        if (wakeMinutes < sleepMinutes) {
            wakeMinutes += 24 * 60;
        }

        const durationMinutes = wakeMinutes - sleepMinutes;
        return (durationMinutes / 60).toFixed(1);
    };

    // Get sleep quality feedback
    const getSleepQualityFeedback = () => {
        const duration = calculateSleepDuration();

        if (duration < 6) {
            return "⚠️ For optimal wellness, consider increasing your sleep duration";
        } else if (duration >= 6 && duration < 7) {
            return "👌 Adequate sleep duration";
        } else if (duration >= 7 && duration <= 9) {
            return "✨ Ideal sleep duration for wellness";
        } else {
            return "⚠️ Extended sleep periods may affect your aromatherapy experience";
        }
    };

    // Get minimum checkout date (day after checkin)
    const getMinCheckoutDate = () => {
        if (!formData.checkInDate) return currentDate;

        const nextDay = new Date(formData.checkInDate);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay.toISOString().split('T')[0];
    };

    // When both dates are pre-filled, validate them
    useEffect(() => {
        if (formData.checkInDatePrefilled && formData.checkOutDatePrefilled) {
            // Check if check-out date is after check-in date
            const checkIn = new Date(formData.checkInDate);
            const checkOut = new Date(formData.checkOutDate);

            if (checkOut <= checkIn) {
                // If invalid, adjust the check-out date
                const nextDay = new Date(checkIn);
                nextDay.setDate(nextDay.getDate() + 1);
                setFormData(prev => ({
                    ...prev,
                    checkOutDate: nextDay.toISOString().split('T')[0]
                }));
            }
        }
    }, [formData.checkInDatePrefilled, formData.checkOutDatePrefilled, formData.checkInDate, formData.checkOutDate]);

    // Calculate sleep time in degrees for visualization
    const getSleepTimeInDegrees = () => {
        if (!formData.sleepTime) return 0;

        const [hours, minutes] = formData.sleepTime.split(':').map(Number);
        // Convert time to degrees (24 hours = 360 degrees)
        return (hours * 15) + (minutes * 0.25); // 15 degrees per hour, 0.25 degrees per minute
    };

    // Calculate sleep arc length and rotation
    const getSleepArcData = () => {
        if (!formData.sleepTime || !formData.wakeUpTime) return { length: 0, rotation: 0 };

        // Get sleep time in degrees
        const sleepDegree = getSleepTimeInDegrees();

        // Calculate duration
        const [sleepHour, sleepMin] = formData.sleepTime.split(':').map(Number);
        const [wakeHour, wakeMin] = formData.wakeUpTime.split(':').map(Number);

        let sleepMinutes = sleepHour * 60 + sleepMin;
        let wakeMinutes = wakeHour * 60 + wakeMin;

        // Handle overnight sleep
        if (wakeMinutes < sleepMinutes) {
            wakeMinutes += 24 * 60;
        }

        // Calculate duration in degrees (24 hours = 360 degrees)
        const durationDegrees = ((wakeMinutes - sleepMinutes) / (24 * 60)) * 360;

        return {
            rotation: sleepDegree,
            length: durationDegrees
        };
    };

    // Calculate sleep arc length for visualization
    const getSleepArcLength = () => {
        if (!formData.sleepTime || !formData.wakeUpTime) return 0;

        const duration = calculateSleepDuration();
        return (duration / 24) * 100; // Percentage of 24 hours
    };

    // Form submission
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!debugging_without_calling_API) {
            if (!validateForm()) {
                return;
            }

            if (!isChecked) {
                alert("You must agree to the terms and conditions before proceeding.");
                return;
            }
        }

        setIsSubmitting(true);

        const email = formData.email.trim() === ""
            ? `empty_email_${new Date().toISOString()}`
            : formData.email;

        const params = new URLSearchParams(window.location.search);
        const utmChannel = params.get("utm_channel");
        const utmUserns = params.get("utm_userns");

        const dataToSubmit = {
            ...formData,
            utmChannel: utmChannel,
            utmUserns: utmUserns,
            email,
            nationality:
                formData.nationality === "Others"
                    ? formData.otherNationality
                    : formData.nationality,
        };

        if (!debugging_without_calling_API) {
            try {
                const response = await fetch(
                    "https://hocyc82op1.execute-api.ap-southeast-1.amazonaws.com/checkin",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization:
                                "Bearer RaXc*lq3j5=1w<$rD^C+}VGvZVi]=dWN}P}(^~XK;h6%O@D;KkqKm~uU>+f,dqY",
                        },
                        body: JSON.stringify(dataToSubmit),
                    }
                );

                if (response.ok) {
                    setShowSuccessPage(true);
                    setFormData(dataToSubmit);
                } else {
                    alert("Failed to submit the form. Please try again.");
                }
            } catch (error) {
                console.error("Error submitting the form:", error);
                alert("An error occurred. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        } else if (debugging_without_calling_API) {
            setTimeout(() => {
                setFormData(dataToSubmit);
                setShowSuccessPage(true);
                setIsSubmitting(false);
            }, 800);
        }
    };

    // Update room number in the database
    const updateRoomNumber = async (email, roomNumber) => {
        if (!debugging_without_calling_API) {
            try {
                const response = await fetch(
                    "https://hocyc82op1.execute-api.ap-southeast-1.amazonaws.com/checkin/edit",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization:
                                "Bearer RaXc*lq3j5=1w<$rD^C+}VGvZVi]=dWN}P}(^~XK;h6%O@D;KkqKm~uU>+f,dqY",
                        },
                        body: JSON.stringify({
                            email,
                            roomNumber,
                        }),
                    }
                );
                console.log(response);
                if (response.ok) {
                    const result = await response.json();
                    setShowSuccessPage(false);
                    setShowLoadingPage(true);
                } else {
                    const errorData = await response.json();
                    alert(`Failed to update room number: ${errorData.message}`);
                }
            } catch (error) {
                console.error("Error updating room number:", error);
                alert("Failed to update room number.");
            }
        } else if (debugging_without_calling_API) {
            setShowSuccessPage(false);
            setShowLoadingPage(true);
        }
    };

    // Room confirmation and success page
    const SuccessPage = ({ onConfirm, onCancel }) => {
        const [inputRoomNumber, setInputRoomNumber] = useState("");
        const [loading, setLoading] = useState(false);
        const [animateIn, setAnimateIn] = useState(false);

        useEffect(() => {
            setTimeout(() => {
                setAnimateIn(true);
            }, 100);
        }, []);

        const fetchGoogleSheetData = async () => {
            try {
                const response = await axios.get(
                    `https://docs.google.com/spreadsheets/d/1fQwkxil-foADfQFMWw9WNi_ms1lfcH1xIA5Omcnn5_M/gviz/tq?tqx=out:json`
                );
                const text = response.data;
                const json = JSON.parse(text.substr(47).slice(0, -2));
                const rows = json.table.rows.map((row) =>
                    row.c.map((cell) => (cell ? cell.v : ""))
                );

                const roomToSerialMapping = rows.reduce((acc, row) => {
                    const serialNumber = row[0];
                    const roomNumber = row[2];
                    if (serialNumber && roomNumber) {
                        acc[roomNumber] = serialNumber;
                    }
                    return acc;
                }, {});

                return roomToSerialMapping;
            } catch (err) {
                console.error("Failed to fetch Google Sheet data:", err.message);
                return {};
            }
        };

        const handleConfirm = async () => {
            if (inputRoomNumber) {
                setLoading(true);
                try {
                    const roomToSerialMapping = await fetchGoogleSheetData();
                    const serial = roomToSerialMapping[inputRoomNumber];

                    if (serial) {
                        setSerialNumber(serial);
                        const confirmed = window.confirm(
                            `Room ${inputRoomNumber} is ready for setup. Confirm? (Ref.: SN-${serial})`
                        );
                        if (confirmed) {
                            onConfirm(inputRoomNumber, serial);
                        }
                    } else {
                        alert(`Room ${inputRoomNumber} is not ready for setup, please enter with the correct room number.`);
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                    alert("Failed to fetch data. Please try again.");
                } finally {
                    setLoading(false);
                }
            } else {
                alert("Please enter a valid room number.");
            }
        };

        return (
            <div className={`success-page ${animateIn ? 'fade-in' : ''}`}>
                <div className="success-icon">✅</div>
                <h1 className="success-heading">Thank you!</h1>
                <p className="success-text">
                    Dear Guest, your Check-In process is almost complete. Please enter your room number, and we will activate the Aromatherapy service for you!
                </p>
                <input
                    type="text"
                    value={inputRoomNumber}
                    onChange={(e) => setInputRoomNumber(e.target.value)}
                    className="success-input"
                    placeholder="Room Number"
                />
                <div className="button-group">
                    <button
                        onClick={handleConfirm}
                        disabled={loading || !inputRoomNumber.trim()}
                        className={`confirm-button ${loading || !inputRoomNumber.trim() ? 'disabled-button' : ''}`}
                    >
                        {loading ?
                            <span className="loading-text">Loading<span className="loading-dots">...</span></span> :
                            "Confirm"
                        }
                    </button>
                    <button onClick={onCancel} className="cancel-button">
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    // Loading page component
    const LoadingPage = ({ roomNumber, serialNumber, formData, onLoadingComplete }) => {
        const [isLoading, setIsLoading] = useState(true);
        const [apiResponse, setApiResponse] = useState(null);
        const [isResultsVisible, setIsResultsVisible] = useState(false);
        const [schedules, setSchedules] = useState([]);
        const [showModal, setShowModal] = useState(false);
        const [isAutoScheduling, setIsAutoScheduling] = useState(false);
        const [countdown, setCountdown] = useState(1);
        const [loadingProgress, setLoadingProgress] = useState(0);
        const [animateIn, setAnimateIn] = useState(false);

        // Entrance animation
        useEffect(() => {
            setTimeout(() => {
                setAnimateIn(true);
            }, 100);

            // Simulate progress animation
            const interval = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        return 100;
                    }
                    return prev + 5;
                });
            }, 150);

            return () => clearInterval(interval);
        }, []);

        // Build API form data
        const buildApiFormData = () => {
            let api_send_time = getISO8601WithTimezone(
                formData.checkInDate,
                new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
            );

            const date = new Date(api_send_time);
            date.setMinutes(date.getMinutes() + 2);
            api_send_time = date.toISOString();

            const params = new URLSearchParams(window.location.search);
            const utmChannel = params.get("utm_channel");
            const utmUserns = params.get("utm_userns");

            const apiFormData = {
                device_sn: serialNumber,
                customer_id: formData?.mobileNumber || "",
                api_send_time: api_send_time,
                checkin_date: formData?.checkInDate || "",
                checkout_date: formData?.checkOutDate || "",
                wakeup_time: formData?.wakeUpTime || "",
                sleep_time: formData?.sleepTime || "",
                morning_aroma: "Metal",
                morning_intensity_level: (formData?.aromaStrength || 0) * 20,
                afternoon_aroma: "Fire",
                afternoon_intensity_level: (formData?.aromaStrength || 0) * 20,
                evening_aroma: "Wood",
                evening_intensity_level: (formData?.aromaStrength || 0) * 20,
                late_night_aroma: "Earth",
                late_night_intensity_level: (formData?.aromaStrength || 0) * 20,
                isConcern: formData?.safetyConcerns === "Yes" ? 1 : 0,
            };
            return apiFormData;
        };

        // Call API when component mounts
        useEffect(() => {
            handleApiRequest();
        }, []);

        // API request handler
        const handleApiRequest = async () => {
            setIsLoading(true);

            const requestData = buildApiFormData();

            try {
                const response = await fetch(
                    "https://1700v3lt3e.execute-api.ap-east-1.amazonaws.com/kl-admin-autoscheduling/v2",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "kl-project-admin",
                        },
                        body: JSON.stringify(requestData),
                    }
                );

                if (response.ok) {
                    const responseData = await response.json();
                    console.log("API 响应:", responseData);

                    if (responseData.schedules && responseData.schedules.length > 0) {
                        setSchedules(responseData.schedules);
                        await handleAutoSchedule(responseData.schedules);
                    }
                    setApiResponse(responseData);
                    setIsResultsVisible(true);
                } else {
                    console.error("API 请求失败:", response.statusText);
                    alert("API 请求失败: " + response.statusText);
                }
            } catch (error) {
                console.error("API 请求错误:", error);
                alert("API 请求失败！");
            } finally {
                setIsLoading(false);
            }
        };

        // Auto scheduling handler
        const handleAutoSchedule = async (schedulesData) => {
            setIsAutoScheduling(true);

            try {
                if (!serialNumber) {
                    alert("Device Sn is required to auto-schedule!");
                    setIsAutoScheduling(false);
                    return;
                }

                for (const schedule of schedulesData) {
                    const response = await fetch(
                        "https://7dqq5knnv0.execute-api.ap-southeast-1.amazonaws.com/add-schedule",
                        {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                serial_number: serialNumber,
                                timezone: "+08:00",
                                start_time: schedule.start_time,
                                end_time: schedule.end_time,
                                aroma_scent: schedule.scheduled_parameters.element,
                                aroma_intensity: schedule.scheduled_parameters.intensity,
                            }),
                        }
                    );

                    if (!response.ok) {
                        console.log(`Failed to add schedule: ${response.status}`);
                    }
                }

                console.log("All schedules added successfully!");

                let url = "";
                const params = new URLSearchParams(window.location.search);
                const utmUserns = params.get("utm_userns");
                const utm_channel = params.get("utm_channel");
                switch (utm_channel?.toLowerCase()) {
                    case "facebook":
                        url = "https://app.usesalesbot.io/api/iwh/145b295b8ce6e132463c39578339bd84";
                        break;
                    case "whatsapp":
                        url = "https://app.usesalesbot.io/api/iwh/82aa336abc0e1595cf3766c3b063dfdd";
                        break;
                    case "webapp":
                        url = "https://app.usesalesbot.io/api/iwh/145b295b8ce6e132463c39578339bd84";
                        break;
                    default:
                        url = null;
                }

                const checkoutDate = formData?.checkOutDate;
                const utcCheckoutDate = new Date(`${checkoutDate}T12:00:00+08:00`);

                if (url) {
                    function formatToHKTime(date) {
                        const d = new Date(date);
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        const hours = String(d.getHours()).padStart(2, '0');
                        const minutes = String(d.getMinutes()).padStart(2, '0');
                        const seconds = String(d.getSeconds()).padStart(2, '0');
                        const milliseconds = String(d.getMilliseconds()).padStart(3, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+08:00`;
                    }

                    const hkTime = formatToHKTime(new Date());
                    const formattedCheckoutDate = formatToHKTime(utcCheckoutDate);

                    const webhookPayload = {
                        utm_userns: utmUserns,
                        checkin_datetime: hkTime,
                        checkout_datetime: formattedCheckoutDate,
                        wakeUpTime: formData?.wakeUpTime,
                        hotelName: formData?.hotelName,
                        isAsthma: formData?.safetyConcerns === "Yes" ? 1 : 0,
                        schedules: schedulesData,
                    };

                    await fetch(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(webhookPayload),
                    });
                    console.log("Webhook called successfully with payload:", webhookPayload);
                }
            } catch (error) {
                console.error("Error during auto-schedule:", error);
                alert("Failed to auto-schedule. Please try again.");
            } finally {
                setIsAutoScheduling(false);
            }
        };

        // Countdown and redirect
        useEffect(() => {
            if (isResultsVisible && countdown > 0) {
                const timer = setTimeout(() => {
                    setCountdown(countdown - 1);
                }, 1000);
                return () => clearTimeout(timer);
            } else if (isResultsVisible && countdown === 0) {
                const params = new URLSearchParams(window.location.search);
                const utmChannel = params.get("utm_channel");
                let url = "";

                switch (utmChannel?.toLowerCase()) {
                    case "facebook":
                        url = "https://app.usesalesbot.io/p/facebook/vzjubqaifiuo5s9q?ref=wf132639w80615";
                        break;
                    case "whatsapp":
                        url = "http://wa.me/+85293789122";
                        break;
                    case "webapp":
                        url = "https://app.usesalesbot.io/p/web/vzjubqaifiuo5s9q?ref=wf132639w80617";
                        break;
                    default:
                        url = "https://www.katlai.com/";
                }

                window.location.href = url;
            }
        }, [isResultsVisible, countdown]);

        return (
            <div className={`loading-container ${animateIn ? 'fade-in' : ''}`}>
                <div className="loading-card">
                    {isLoading ? (
                        <>
                            <div className="loading-spinner"></div>
                            <h2 className="loading-heading">Setting up your experience...</h2>
                            <p className="loading-text">We are configuring your personalized aromatherapy for room {roomNumber}.</p>
                            <p className="loading-text">Please do not leave this site and wait for moments.</p>
                            <div className="loading-progress-bar">
                                <div className="loading-progress-fill" style={{ width: `${loadingProgress}%` }}></div>
                            </div>
                        </>
                    ) : isResultsVisible ? (
                        <div className="completion-message fade-in">
                            <div className="success-icon pulse">✨</div>
                            <h2 className="completion-heading">Setup Complete!</h2>
                            <p className="completion-text">Schedules have been successfully created for your stay.</p>
                            <p className="countdown-text">Redirecting in <span className="countdown-number">{countdown}</span> seconds...</p>
                        </div>
                    ) : (
                        <div className="error-message fade-in">
                            <div className="error-icon">❌</div>
                            <h2>An error occurred</h2>
                            <p>Please try again or contact hotel staff for assistance.</p>
                            <button
                                className="retry-button"
                                onClick={() => handleApiRequest()}
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="duolingo-style-container">
            {showLoadingPage ? (
                <LoadingPage
                    roomNumber={roomNumber}
                    serialNumber={serialNumber}
                    formData={formData}
                    onLoadingComplete={() => setShowLoadingPage(false)}
                />
            ) : showSuccessPage ? (
                <SuccessPage
                    onConfirm={(roomNumber, serialNumber) => updateRoomNumber(formData.email, roomNumber, serialNumber)}
                    onCancel={() => setShowSuccessPage(false)}
                />
            ) : (
                <div className="step-by-step-form">
                    {/* Progress Bar */}
                    {currentStep > 0 && (
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                            <div className="step-indicator">
                                <span className="current-step">{currentStep}</span>
                                <span className="step-divider">/</span>
                                <span className="total-steps">{formSteps.length - 1}</span>
                            </div>
                        </div>
                    )}

                    {/* Content Area with Animation */}
                    <div ref={contentRef} className="content-container">
                        <div className="content-area">
                            {currentStep === 0 && (
                                <div className="form-step welcome-step">
                                    <div className="welcome-image"></div>
                                    <h1 className="welcome-title">Welcome to Your Premium Aromatherapy Experience</h1>
                                    <p className="welcome-description">
                                        Get ready to personalize your wellness journey during your stay with us.
                                        Complete this quick setup to activate your exclusive aromatherapy service.
                                    </p>
                                    <div className="feature-list">
                                        <div className="feature-item">
                                            <div className="feature-icon">🌿</div>
                                            <div className="feature-text">
                                                <h3>Personalized Aromatherapy</h3>
                                                <p>Tailored to your sleep patterns and preferences</p>
                                            </div>
                                        </div>
                                        <div className="feature-item">
                                            <div className="feature-icon">💤</div>
                                            <div className="feature-text">
                                                <h3>Better Sleep Quality</h3>
                                                <p>Experience improved relaxation and sleep</p>
                                            </div>
                                        </div>
                                        <div className="feature-item">
                                            <div className="feature-icon">✨</div>
                                            <div className="feature-text">
                                                <h3>Exclusive Upgrade</h3>
                                                <p>Complimentary wellness room enhancement</p>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="action-button pulse-animation" onClick={goToNextStep}>
                                        Start Setup
                                    </button>
                                </div>
                            )}

                            {currentStep === 1 && (
                                <div className="form-step personal-step">
                                    <h2 className="step-title">Tell Us About Yourself</h2>
                                    <p className="step-description">We'll use this information to personalize your experience</p>

                                    <div className={`input-group ${errors.name ? 'input-error' : ''} ${formData.name ? 'input-filled' : ''}`}>
                                        <label className="input-label">
                                            Name <span className="required-mark">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="elegant-input"
                                            placeholder="Your full name"
                                        />
                                        {errors.name && <p className="error-message">{errors.name}</p>}
                                    </div>

                                    <div className={`input-group ${errors.email ? 'input-error' : ''} ${formData.email ? 'input-filled' : ''}`}>
                                        <label className="input-label">
                                            Email <span className="required-mark">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="elegant-input"
                                            placeholder="your.email@example.com"
                                        />
                                        {errors.email && <p className="error-message">{errors.email}</p>}
                                    </div>

                                    <div className="row-group">
                                        <div className="column-group">
                                            <label className="input-label">
                                                Gender <span className="required-mark">*</span>
                                            </label>
                                            <div className="elegant-radio-group">
                                                <div
                                                    className={`elegant-radio ${formData.gender === "Male" ? 'elegant-radio-active' : ''}`}
                                                    onClick={() => setFormData(prev => ({ ...prev, gender: "Male" }))}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        value="Male"
                                                        checked={formData.gender === "Male"}
                                                        onChange={handleChange}
                                                        style={{ position: "absolute", opacity: 0 }}
                                                    />
                                                    Male
                                                </div>
                                                <div
                                                    className={`elegant-radio ${formData.gender === "Female" ? 'elegant-radio-active' : ''}`}
                                                    onClick={() => setFormData(prev => ({ ...prev, gender: "Female" }))}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        value="Female"
                                                        checked={formData.gender === "Female"}
                                                        onChange={handleChange}
                                                        style={{ position: "absolute", opacity: 0 }}
                                                    />
                                                    Female
                                                </div>
                                            </div>
                                            {errors.gender && <p className="error-message">{errors.gender}</p>}
                                        </div>

                                        <div className="column-group">
                                            <label className="input-label">
                                                Birthday (YYYYMMDD) <span className="required-mark">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="birthday"
                                                value={formData.birthday}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,8}$/.test(value)) {
                                                        setFormData((prev) => ({ ...prev, birthday: value }));
                                                    }
                                                }}
                                                className={`elegant-input ${errors.birthday ? 'input-error' : ''} ${formData.birthday ? 'input-filled' : ''}`}
                                                placeholder="YYYYMMDD"
                                            />
                                            {errors.birthday && <p className="error-message">{errors.birthday}</p>}
                                        </div>
                                    </div>

                                    <div className={`input-group ${errors.nationality ? 'input-error' : ''} ${formData.nationality ? 'input-filled' : ''}`}>
                                        <label className="input-label">
                                            Nationality <span className="required-mark">*</span>
                                        </label>
                                        <select
                                            name="nationality"
                                            value={formData.nationality === "Others" ? "Others" : formData.nationality}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                const [country, code] = selectedValue.split("|");
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    nationality: selectedValue,
                                                    mobileNumber: code || "",
                                                }));
                                            }}
                                            className="elegant-select"
                                        >
                                            <option value="">Select Nationality</option>
                                            <option value="American|+1">American (+1)</option>
                                            <option value="Australian|+61">Australian (+61)</option>
                                            <option value="Brazilian|+55">Brazilian (+55)</option>
                                            <option value="Canadian|+1">Canadian (+1)</option>
                                            <option value="Chinese|+86">Chinese (+86)</option>
                                            <option value="Filipino|+63">Filipino (+63)</option>
                                            <option value="Hong Kong|+852">Hong Kong (+852)</option>
                                            <option value="Indian|+91">Indian (+91)</option>
                                            <option value="Japanese|+81">Japanese (+81)</option>
                                            <option value="Korean|+82">Korean (+82)</option>
                                            <option value="Malaysian|+60">Malaysian (+60)</option>
                                            <option value="Singaporean|+65">Singaporean (+65)</option>
                                            <option value="Thai|+66">Thai (+66)</option>
                                            <option value="Vietnamese|+84">Vietnamese (+84)</option>
                                            <option value="Others">Others</option>
                                        </select>
                                        {errors.nationality && <p className="error-message">{errors.nationality}</p>}
                                    </div>

                                    {formData.nationality === "Others" && (
                                        <div className={`input-group slide-in ${formData.otherNationality ? 'input-filled' : ''}`}>
                                            <label className="input-label">If Others, please specify</label>
                                            <input
                                                type="text"
                                                name="otherNationality"
                                                value={formData.otherNationality || ""}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, otherNationality: e.target.value }))
                                                }
                                                className="elegant-input"
                                                placeholder="Enter your nationality"
                                            />
                                        </div>
                                    )}

                                    <div className={`input-group ${errors.mobileNumber ? 'input-error' : ''} ${formData.mobileNumber ? 'input-filled' : ''}`}>
                                        <label className="input-label">
                                            Mobile Number <span className="required-mark">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="mobileNumber"
                                            value={formData.mobileNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\+?\d*$/.test(value)) {
                                                    setFormData((prev) => ({ ...prev, mobileNumber: value }));
                                                }
                                            }}
                                            className="elegant-input"
                                            placeholder="e.g., +85212345678"
                                        />
                                        {errors.mobileNumber && <p className="error-message">{errors.mobileNumber}</p>}
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="form-step stay-step">
                                    <h2 className="step-title">Your Stay Details</h2>
                                    <p className="step-description">This helps us schedule your aromatherapy experience</p>

                                    {/* Improved Hotel Name Field with Autocomplete */}
                                    <div className={`input-group hotel-input-group ${errors.hotelName ? 'input-error' : ''} ${formData.hotelName ? 'input-filled' : ''}`}>
                                        <label className="input-label">
                                            Hotel Name <span className="required-mark">*</span>
                                        </label>
                                        <div className="hotel-autocomplete-container">
                                            <input
                                                type="text"
                                                name="hotelName"
                                                value={formData.hotelName}
                                                onChange={(e) => {
                                                    // Only allow changes if not pre-filled
                                                    if (!formData.hotelNamePrefilled) {
                                                        handleChange(e);
                                                        setPopularHotels(suggestHotels(e.target.value));
                                                        if (e.target.value.length > 2) {
                                                            setShowHotelSuggestions(true);
                                                        } else {
                                                            setShowHotelSuggestions(false);
                                                        }
                                                    }
                                                }}
                                                className={`elegant-input ${formData.hotelNamePrefilled ? 'prefilled-input' : ''}`}
                                                placeholder="Enter the name of your current hotel"
                                                readOnly={formData.hotelNamePrefilled}
                                            />
                                            {/* Show pre-filled badge if applicable */}
                                            {formData.hotelNamePrefilled && (
                                                <div className="prefilled-badge">
                                                    <span>✓ Pre-filled</span>
                                                </div>
                                            )}
                                            {/* Suggestions code... */}
                                        </div>
                                        {errors.hotelName && <p className="error-message">{errors.hotelName}</p>}
                                    </div>

                                    {/* Improved Date Selection */}
                                    <div className="date-selection-container">
                                        <div className="date-column">
                                            <label className="input-label">
                                                Check-In Date 📅 <span className="required-mark">*</span>
                                            </label>
                                            <div className="date-picker-wrapper">
                                                <input
                                                    type="date"
                                                    name="checkInDate"
                                                    value={formData.checkInDate}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => {
                                                        // Only allow changes if not pre-filled
                                                        if (!formData.checkInDatePrefilled) {
                                                            handleChange(e);
                                                            // Auto update checkout to be at least next day if needed
                                                            const nextDay = new Date(e.target.value);
                                                            nextDay.setDate(nextDay.getDate() + 1);
                                                            const nextDayStr = nextDay.toISOString().split('T')[0];
                                                            if (!formData.checkOutDate || new Date(formData.checkOutDate) <= new Date(e.target.value)) {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    checkOutDate: nextDayStr
                                                                }));
                                                            }
                                                        }
                                                    }}
                                                    className={`date-input ${errors.checkInDate ? 'input-error' : ''} ${formData.checkInDate ? 'input-filled' : ''} ${formData.checkInDatePrefilled ? 'prefilled-input' : ''}`}
                                                    readOnly={formData.checkInDatePrefilled}
                                                />
                                                {formData.checkInDatePrefilled && (
                                                    <div className="prefilled-badge date-prefilled-badge">
                                                        <span>✓ Pre-filled</span>
                                                    </div>
                                                )}
                                            </div>
                                            {errors.checkInDate && <p className="error-message">{errors.checkInDate}</p>}
                                        </div>

                                        <div className="date-column">
                                            <label className="input-label">
                                                Check-Out Date 📅 <span className="required-mark">*</span>
                                            </label>
                                            <div className="date-picker-wrapper">
                                                <input
                                                    type="date"
                                                    name="checkOutDate"
                                                    value={formData.checkOutDate}
                                                    min={getMinCheckoutDate()}
                                                    onChange={(e) => {
                                                        // Only allow changes if not pre-filled
                                                        if (!formData.checkOutDatePrefilled) {
                                                            handleChange(e);
                                                        }
                                                    }}
                                                    className={`date-input ${errors.checkOutDate ? 'input-error' : ''} ${formData.checkOutDate ? 'input-filled' : ''} ${formData.checkOutDatePrefilled ? 'prefilled-input' : ''}`}
                                                    readOnly={formData.checkOutDatePrefilled}
                                                />
                                                {formData.checkOutDatePrefilled && (
                                                    <div className="prefilled-badge date-prefilled-badge">
                                                        <span>✓ Pre-filled</span>
                                                    </div>
                                                )}
                                            </div>
                                            {errors.checkOutDate && <p className="error-message">{errors.checkOutDate}</p>}
                                        </div>
                                    </div>

                                    {/* Stay duration indicator */}
                                    {formData.checkInDate && formData.checkOutDate && (
                                        <div className="stay-duration">
                                            <span className="duration-label">Duration of Stay:</span>
                                            <span className="duration-value">{calculateStayDuration()} nights</span>
                                        </div>
                                    )}

                                    {/* Improved Time Selection */}
                                    <div className="time-selection-section">
                                        <h3 className="time-section-title">Your Sleep Schedule</h3>
                                        <p className="time-section-desc">This helps us optimize aromatherapy timing for your sleep cycle</p>

                                        <div className="time-selection-row">
                                            <div className="time-picker-container">
                                                <label className="input-label">
                                                    Sleep Time 🌙 <span className="required-mark">*</span>
                                                    <span className="label-hint">When you typically go to bed</span>
                                                </label>

                                                <div className="time-picker-ui">
                                                    <div className="time-presets">
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.sleepTime === "21:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, sleepTime: "21:00" }))}
                                                        >
                                                            9 PM <span className="time-icon">🌙</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.sleepTime === "22:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, sleepTime: "22:00" }))}
                                                        >
                                                            10 PM <span className="time-icon">🌙</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.sleepTime === "23:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, sleepTime: "23:00" }))}
                                                        >
                                                            11 PM <span className="time-icon">🌙</span>
                                                        </button>
                                                    </div>

                                                    <div className="time-input-wrapper">
                                                        <input
                                                            type="time"
                                                            name="sleepTime"
                                                            value={formData.sleepTime}
                                                            onChange={handleChange}
                                                            className={`time-input ${errors.sleepTime ? 'input-error' : ''}`}
                                                        />
                                                    </div>
                                                </div>
                                                {errors.sleepTime && <p className="error-message">{errors.sleepTime}</p>}
                                            </div>

                                            <div className="time-picker-container">
                                                <label className="input-label">
                                                    Wake Up Time <span className="required-mark">*</span>
                                                    <span className="label-hint">When you typically wake up</span>
                                                </label>

                                                <div className="time-picker-ui">
                                                    <div className="time-presets">
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.wakeUpTime === "06:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, wakeUpTime: "06:00" }))}
                                                        >
                                                            6 AM <span className="time-icon">☀️</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.wakeUpTime === "07:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, wakeUpTime: "07:00" }))}
                                                        >
                                                            7 AM <span className="time-icon">☀️</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`time-preset-btn ${formData.wakeUpTime === "08:00" ? "selected" : ""}`}
                                                            onClick={() => setFormData(prev => ({ ...prev, wakeUpTime: "08:00" }))}
                                                        >
                                                            8 AM <span className="time-icon">☀️</span>
                                                        </button>
                                                    </div>

                                                    <div className="time-input-wrapper">
                                                        <input
                                                            type="time"
                                                            name="wakeUpTime"
                                                            value={formData.wakeUpTime}
                                                            onChange={handleChange}
                                                            className={`time-input ${errors.wakeUpTime ? 'input-error' : ''}`}
                                                        />
                                                    </div>
                                                </div>
                                                {errors.wakeUpTime && <p className="error-message">{errors.wakeUpTime}</p>}
                                            </div>
                                        </div>

                                        {/* Sleep duration indicator */}
                                        {formData.sleepTime && formData.wakeUpTime && (
                                            <div className="sleep-duration-indicator">
                                                <div className="sleep-duration-label">Estimated Sleep Duration:</div>
                                                <div className="sleep-duration-value">{calculateSleepDuration()} hours</div>
                                                <div className="sleep-quality-indicator">
                                                    {getSleepQualityFeedback()}
                                                </div>
                                            </div>
                                        )}

                                        {formData.sleepTime && formData.wakeUpTime && (
                                            <div className="sleep-cycle-visualization">
                                                <h4 className="visualization-title">Your Sleep Cycle</h4>
                                                <div className="visualization-description">
                                                    Our aromatherapy will automatically adjust to complement your sleep routine
                                                </div>
                                                <div className="circle-clock-container">
                                                    <svg viewBox="0 0 220 220" className="sleep-cycle-svg">
                                                        {/* Clock face */}
                                                        <circle cx="110" cy="110" r="100" fill="transparent" stroke="#dfe4ea" strokeWidth="2" />

                                                        {/* Day/Night indicators */}
                                                        <path d="M110,10 A100,100 0 0,1 210,110" fill="transparent" stroke="rgba(52, 73, 94, 0.2)" strokeWidth="6" />
                                                        <path d="M110,210 A100,100 0 0,1 10,110" fill="transparent" stroke="rgba(255, 193, 7, 0.2)" strokeWidth="6" />

                                                        {/* Hour markers */}
                                                        {Array.from({ length: 24 }).map((_, i) => {
                                                            const angle = (i * 15) * Math.PI / 180;
                                                            const x1 = 110 + 90 * Math.sin(angle);
                                                            const y1 = 110 - 90 * Math.cos(angle);
                                                            const x2 = 110 + 100 * Math.sin(angle);
                                                            const y2 = 110 - 100 * Math.cos(angle);
                                                            return (
                                                                <g key={i}>
                                                                    <line
                                                                        x1={x1} y1={y1}
                                                                        x2={x2} y2={y2}
                                                                        stroke="#dfe4ea"
                                                                        strokeWidth="1"
                                                                    />
                                                                    <text
                                                                        x={110 + 80 * Math.sin(angle)}
                                                                        y={110 - 80 * Math.cos(angle)}
                                                                        textAnchor="middle"
                                                                        dominantBaseline="central"
                                                                        fill="#7F8C8D"
                                                                        fontSize="10"
                                                                    >
                                                                        {i}
                                                                    </text>
                                                                </g>
                                                            );
                                                        })}

                                                        {/* Sleep arc */}
                                                        {formData.sleepTime && formData.wakeUpTime && (() => {
                                                            const sleepData = getSleepArcData();
                                                            const startAngle = (sleepData.rotation - 90) * Math.PI / 180;
                                                            const endAngle = (sleepData.rotation + sleepData.length - 90) * Math.PI / 180;

                                                            const startX = 110 + 95 * Math.cos(startAngle);
                                                            const startY = 110 + 95 * Math.sin(startAngle);
                                                            const endX = 110 + 95 * Math.cos(endAngle);
                                                            const endY = 110 + 95 * Math.sin(endAngle);

                                                            // Determine if arc is more than half the circle
                                                            const largeArcFlag = sleepData.length > 180 ? 1 : 0;

                                                            return (
                                                                <path
                                                                    d={`M ${startX} ${startY} A 95 95 0 ${largeArcFlag} 1 ${endX} ${endY}`}
                                                                    fill="transparent"
                                                                    stroke="#3498DB"
                                                                    strokeWidth="6"
                                                                />
                                                            );
                                                        })()}

                                                        {/* Center dot */}
                                                        <circle cx="110" cy="110" r="4" fill="#3498DB" />

                                                        {/* Icons */}
                                                        {/* <text x="110" y="20" textAnchor="middle" fontSize="16">☀️</text>
            <text x="110" y="200" textAnchor="middle" fontSize="16">🌙</text> */}
                                                    </svg>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Contextual Information */}
                                    <div className="info-card">
                                        <div className="info-icon">💡</div>
                                        <div className="info-content">
                                            <h4 className="info-title">How Your Schedule Affects Your Experience</h4>
                                            <p className="info-text">
                                                Your sleep and wake times help us schedule specific aromatherapy scents
                                                that align with your natural rhythm. Morning scents promote alertness, while
                                                evening blends encourage relaxation.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 3 && (
                                <div className="form-step preferences-step">
                                    <h2 className="step-title">Aromatherapy Preferences</h2>
                                    <p className="step-description">Tell us about your wellness needs for a personalized experience</p>

                                    <div className={`input-group ${errors.sleepProblem ? 'input-error' : ''}`}>
                                        <label className="input-label">
                                            Any Sleep Problem? <span className="required-mark">*</span>
                                        </label>
                                        <div className="elegant-radio-group">
                                            <div
                                                className={`elegant-radio ${formData.sleepProblem === "Yes" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, sleepProblem: "Yes" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="sleepProblem"
                                                    value="Yes"
                                                    checked={formData.sleepProblem === "Yes"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                Yes
                                            </div>
                                            <div
                                                className={`elegant-radio ${formData.sleepProblem === "No" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, sleepProblem: "No" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="sleepProblem"
                                                    value="No"
                                                    checked={formData.sleepProblem === "No"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                No
                                            </div>
                                        </div>
                                        {errors.sleepProblem && <p className="error-message">{errors.sleepProblem}</p>}
                                    </div>

                                    {formData.sleepProblem === "Yes" && (
                                        <div className={`input-group slide-in ${formData.sleepSolution ? 'input-filled' : ''}`}>
                                            <label className="input-label">If yes, please describe your concerns</label>
                                            <textarea
                                                name="sleepSolution"
                                                value={formData.sleepSolution}
                                                onChange={handleChange}
                                                className="elegant-textarea"
                                                placeholder="E.g., difficulty falling asleep, waking up during the night, etc."
                                            />
                                        </div>
                                    )}

                                    <div className={`input-group ${errors.aromaStrength ? 'input-error' : ''}`}>
                                        <label className="input-label">
                                            Preferred Aroma Intensity <span className="required-mark">*</span>
                                            <span className="label-hint">1 (subtle) to 5 (intense)</span>
                                        </label>
                                        <div className="intensity-selector">
                                            {formData.aromaStrength && (
                                                <div
                                                    className="intensity-slider"
                                                    style={{
                                                        left: `${(parseInt(formData.aromaStrength) - 1) * 20}%`,
                                                        width: '20%'
                                                    }}
                                                ></div>
                                            )}

                                            {Array.from({ length: 5 }, (_, i) => (
                                                <div
                                                    key={i + 1}
                                                    className={`intensity-option ${formData.aromaStrength === String(i + 1) ? 'intensity-option-active' : ''}`}
                                                    onClick={() => setFormData(prev => ({ ...prev, aromaStrength: String(i + 1) }))}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="aromaStrength"
                                                        value={i + 1}
                                                        checked={formData.aromaStrength === String(i + 1)}
                                                        onChange={handleChange}
                                                        style={{ position: 'absolute', opacity: 0 }}
                                                    />
                                                    <span className="intensity-value">{i + 1}</span>
                                                    <span className="intensity-label">
                                                        {i === 0 ? 'Subtle' : i === 4 ? 'Intense' : ''}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        {errors.aromaStrength && <p className="error-message">{errors.aromaStrength}</p>}
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Health & Safety <span className="required-mark">*</span>
                                        </label>
                                        <p className="field-description">
                                            Are any guests in your room under 6, pregnant, or with respiratory/allergy concerns?
                                        </p>
                                        <div className="elegant-radio-group">
                                            <div
                                                className={`elegant-radio ${formData.safetyConcerns === "Yes" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, safetyConcerns: "Yes" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="safetyConcerns"
                                                    value="Yes"
                                                    checked={formData.safetyConcerns === "Yes"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                Yes
                                            </div>
                                            <div
                                                className={`elegant-radio ${formData.safetyConcerns === "No" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, safetyConcerns: "No" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="safetyConcerns"
                                                    value="No"
                                                    checked={formData.safetyConcerns === "No"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                No
                                            </div>
                                        </div>
                                        {errors.safetyConcerns && <p className="error-message">{errors.safetyConcerns}</p>}
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Have you used aromatherapy before?
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <div className="elegant-radio-group">
                                            <div
                                                className={`elegant-radio ${formData.useAroma === "Yes" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, useAroma: "Yes" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="useAroma"
                                                    value="Yes"
                                                    checked={formData.useAroma === "Yes"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                Yes
                                            </div>
                                            <div
                                                className={`elegant-radio ${formData.useAroma === "No" ? 'elegant-radio-active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, useAroma: "No" }))}
                                            >
                                                <input
                                                    type="radio"
                                                    name="useAroma"
                                                    value="No"
                                                    checked={formData.useAroma === "No"}
                                                    onChange={handleChange}
                                                    style={{ position: "absolute", opacity: 0 }}
                                                />
                                                No
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 4 && (
                                <div className="form-step enhance-step">
                                    <h2 className="step-title">Enhance Your Experience</h2>
                                    <p className="step-description">Optional information to further personalize your stay</p>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Who are you traveling with?
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <select
                                            name="travelWith"
                                            value={formData.travelWith}
                                            onChange={handleChange}
                                            className={`elegant-select ${formData.travelWith ? 'select-filled' : ''}`}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="Alone">Alone</option>
                                            <option value="Couple">Couple</option>
                                            <option value="Family">Family</option>
                                            <option value="Friends">Friends</option>
                                            <option value="Groups">Groups</option>
                                            <option value="Colleague">Colleague</option>
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Current well-being
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <select
                                            name="wellBeing"
                                            value={formData.wellBeing}
                                            onChange={handleChange}
                                            className={`elegant-select ${formData.wellBeing ? 'select-filled' : ''}`}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="Energetic">😊 Energetic & refreshed</option>
                                            <option value="Neutral">😐 Neutral, could use a lift</option>
                                            <option value="Fatigued">😴 Fatigued, needing rejuvenation</option>
                                            <option value="Stressed">😖 Stressed, seeking calm</option>
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Wellness priority for this stay
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <select
                                            name="wellnessPriority"
                                            value={formData.wellnessPriority}
                                            onChange={handleChange}
                                            className={`elegant-select ${formData.wellnessPriority ? 'select-filled' : ''}`}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="Sleep">💤 Deep, uninterrupted sleep</option>
                                            <option value="Relaxation">🧘 Full-body relaxation</option>
                                            <option value="Focus">🎯 Mental clarity & focus</option>
                                            <option value="Joy">😊 Mood enhancement & joy</option>
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Purpose of your visit
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <select
                                            name="visitReason"
                                            value={formData.visitReason}
                                            onChange={handleChange}
                                            className={`elegant-select ${formData.visitReason ? 'select-filled' : ''}`}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="Business">💼 Business (productivity focus)</option>
                                            <option value="Romantic">💑 Romantic getaway</option>
                                            <option value="Family">👨‍👩‍👧‍👦 Family bonding</option>
                                            <option value="Health">🍃 Health retreat</option>
                                            <option value="Solo">🧘 Solo recharge</option>
                                        </select>
                                    </div>

                                    <div className="input-group">
                                        <label className="input-label">
                                            Additional requests or concerns
                                            <span className="optional-tag">Optional</span>
                                        </label>
                                        <textarea
                                            name="remarks"
                                            value={formData.remarks}
                                            onChange={handleChange}
                                            className={`elegant-textarea ${formData.remarks ? 'input-filled' : ''}`}
                                            placeholder="Is there anything else you'd like us to know to enhance your experience?"
                                        />
                                    </div>
                                </div>
                            )}

                            {currentStep === 5 && (
                                <div className="form-step review-step">
                                    <h2 className="step-title">Review & Activate</h2>
                                    <p className="step-description">You're almost done! Review your information below</p>

                                    <div className="summary-section">
                                        <h3 className="summary-title">Personal Information</h3>
                                        <div className="summary-grid">
                                            <div className="summary-item">
                                                <span className="summary-label">Name:</span>
                                                <span className="summary-value">{formData.name}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Email:</span>
                                                <span className="summary-value">{formData.email}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Phone:</span>
                                                <span className="summary-value">{formData.mobileNumber}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section">
                                        <h3 className="summary-title">Stay Details</h3>
                                        <div className="summary-grid">
                                            <div className="summary-item">
                                                <span className="summary-label">Hotel:</span>
                                                <span className="summary-value">{formData.hotelName}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Check-in:</span>
                                                <span className="summary-value">{formData.checkInDate}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Check-out:</span>
                                                <span className="summary-value">{formData.checkOutDate}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Sleep Time:</span>
                                                <span className="summary-value">{formData.sleepTime}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Wake Time:</span>
                                                <span className="summary-value">{formData.wakeUpTime}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section">
                                        <h3 className="summary-title">Your Preferences</h3>
                                        <div className="summary-grid">
                                            <div className="summary-item">
                                                <span className="summary-label">Sleep Issues:</span>
                                                <span className="summary-value">{formData.sleepProblem}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Aroma Intensity:</span>
                                                <span className="summary-value">{formData.aromaStrength}/5</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Health Concerns:</span>
                                                <span className="summary-value">{formData.safetyConcerns}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="terms-container">
                                        <input
                                            type="checkbox"
                                            id="termsCheckbox"
                                            checked={isChecked}
                                            onChange={(e) => setIsChecked(e.target.checked)}
                                            className="terms-checkbox"
                                        />
                                        <label htmlFor="termsCheckbox" className="terms-label">
                                            I agree to the{" "}
                                            <a
                                                href="https://www.katlai.com/app-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="terms-link"
                                            >
                                                terms and conditions
                                            </a>
                                        </label>
                                    </div>

                                    <div className="congrats-message">
                                        <div className="congrats-icon">🎉</div>
                                        <p className="congrats-title">
                                            Congratulations!
                                        </p>
                                        <p className="congrats-text">
                                            You will be upgraded to our exclusive <strong>Premium Wellness Sleep Room!</strong>
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="navigation-buttons">
                        {/* Back button - hidden on first step */}
                        {currentStep > 0 && (
                            <button
                                type="button"
                                onClick={goToPreviousStep}
                                className="prev-button"
                                disabled={isSubmitting}
                            >
                                Back
                            </button>
                        )}

                        {/* Next/Submit button logic - no Continue button on step 0 */}
                        {currentStep > 0 && (
                            currentStep < formSteps.length - 1 ? (
                                <button
                                    type="button"
                                    onClick={goToNextStep}
                                    className="next-button"
                                    disabled={isSubmitting}
                                >
                                    Continue
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="submit-button"
                                    disabled={isSubmitting || !isChecked}
                                >
                                    {isSubmitting ? (
                                        <span className="loading-text">Activating<span className="loading-dots">...</span></span>
                                    ) : (
                                        "Activate My Experience"
                                    )}
                                </button>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Inline CSS */}
            <style jsx>{`
                /* Base styles and animations */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes slideIn {
                    from { transform: translateX(-20px); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                @keyframes wiggle {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }
                
                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }
                
                @keyframes loadingDots {
                    0% { content: "."; }
                    33% { content: ".."; }
                    66% { content: "..."; }
                    100% { content: "."; }
                }
                
                @keyframes rotateClock {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .duolingo-style-container {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    font-family: 'Montserrat', 'Helvetica Neue', sans-serif;
                    background: linear-gradient(rgba(248, 249, 250, 0.9), rgba(248, 249, 250, 0.9)), 
                                url('https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80');
                    background-size: cover;
                    background-position: center;
                    background-attachment: fixed;
                }
                
                .step-by-step-form {
                    max-width: 800px;
                    width: 100%;
                    background-color: #ffffff;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .circle-clock-container {
                    display: flex;
                    justify-content: center;
                    margin: 20px 0;
                }

                .sleep-cycle-svg {
                    width: 220px;
                    height: 220px;
                    max-width: 100%;
                }

                @media (max-width: 768px) {
                    .sleep-cycle-svg {
                        width: 180px;
                        height: 180px;
                    }
                }

                .prefilled-input {
                    background-color: #F3F8FD;
                    border-color: #3498DB;
                    color: #2C3E50;
                    font-weight: 500;
                    cursor: not-allowed;
                }

                .prefilled-section {
                    position: relative;
                    background-color: rgba(52, 152, 219, 0.05);
                    border-left: 3px solid #3498DB;
                    padding-left: 15px;
                    margin-left: -15px;
                }

                .prefilled-badge {
                    position: absolute;
                    top: -10px;
                    right: 10px;
                    background-color: #16A085;
                    color: white;
                    font-size: 11px;
                    padding: 3px 8px;
                    border-radius: 10px;
                    font-weight: 500;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .hotel-autocomplete-container {
                    position: relative;
                }
                
                /* Progress bar styling */
                .progress-container {
                    height: 8px;
                    background-color: #ECF0F1;
                    position: relative;
                    margin-bottom: 10px;
                }
                
                .progress-bar {
                    height: 100%;
                    background: linear-gradient(90deg, #3498DB, #9b59b6);
                    transition: width 0.5s ease;
                }
                
                .step-indicator {
                    position: absolute;
                    top: -25px;
                    right: 20px;
                    background-color: #3498DB;
                    color: white;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: bold;
                    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
                }
                
                /* Content area styling */
                .content-container {
                    padding: 30px;
                    transition: all 0.3s ease;
                }
                
                .content-area {
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.5s ease, transform 0.5s ease;
                }
                
                .content-visible .content-area {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                /* Step styling */
                .form-step {
                    animation: fadeIn 0.5s ease-out;
                }
                
                .step-title {
                    font-size: 24px;
                    color: #2C3E50;
                    margin-bottom: 10px;
                    font-weight: 600;
                    font-family: 'Playfair Display', serif;
                }
                
                .step-description {
                    font-size: 16px;
                    color: #7F8C8D;
                    margin-bottom: 30px;
                }

                .date-prefilled-badge {
                    top: -10px;
                    right: 40px; /* Position differently to account for calendar icon */
                }

                .date-picker-wrapper {
                    position: relative;
                }
                
                /* Welcome step specific styling */
                .welcome-step {
                    text-align: center;
                    padding: 20px 0;
                }
                
                .welcome-image {
                    width: 200px;
                    height: 200px;
                    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%233498DB"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>');
                    background-size: contain;
                    background-position: center;
                    background-repeat: no-repeat;
                    margin: 0 auto 30px;
                    animation: pulse 2s infinite;
                }
                
                .welcome-title {
                    font-size: 28px;
                    color: #2C3E50;
                    margin-bottom: 15px;
                    font-weight: 600;
                    font-family: 'Playfair Display', serif;
                }
                
                .welcome-description {
                    font-size: 16px;
                    color: #7F8C8D;
                    margin-bottom: 30px;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                }

                .navigation-buttons {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 30px;
                }

                /* For when there's only one button, ensure proper alignment */
                .navigation-buttons:has(.prev-button:only-child) {
                    justify-content: flex-start;
                }

                .navigation-buttons:has(.next-button:only-child),
                .navigation-buttons:has(.submit-button:only-child) {
                    justify-content: flex-end;
                }

                .button-spacer {
                    /* Width equivalent to your buttons for spacing if needed */
                    width: 120px; /* Adjust to match your button width */
                    height: 1px;
                }
                
                .feature-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    margin-bottom: 30px;
                }
                
                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 15px;
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    transition: transform 0.3s ease;
                }
                
                .feature-item:hover {
                    transform: translateX(5px);
                }
                
                .feature-icon {
                    font-size: 30px;
                    width: 50px;
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    background-color: #EBF5FB;
                }
                
                .feature-text {
                    flex: 1;
                    text-align: left;
                }
                
                .feature-text h3 {
                    margin: 0;
                    font-size: 18px;
                    color: #2C3E50;
                    margin-bottom: 5px;
                }
                
                .feature-text p {
                    margin: 0;
                    font-size: 14px;
                    color: #7F8C8D;
                }
                
                /* Base form element styling */
                .input-group {
                    margin-bottom: 25px;
                    transition: all 0.3s ease;
                }
                
                .input-label {
                    display: block;
                    font-size: 15px;
                    font-weight: 600;
                    color: #34495E;
                    margin-bottom: 8px;
                    font-family: 'Montserrat', sans-serif;
                }
                
                .required-mark {
                    color: #E74C3C;
                    margin-left: 2px;
                }
                
                .optional-tag {
                    font-size: 11px;
                    font-weight: 500;
                    color: #95A5A6;
                    margin-left: 8px;
                    padding: 2px 6px;
                    border-radius: 4px;
                    background-color: #f8f9fa;
                }
                
                .label-hint {
                    display: block;
                    font-size: 12px;
                    font-weight: 400;
                    color: #7F8C8D;
                    margin-top: 2px;
                }
                
                .elegant-input, .elegant-select, .elegant-textarea {
                    width: 94%;
                    padding: 12px 10px;
                    border: 1px solid #dfe4ea;
                    border-radius: 6px;
                    font-size: 15px;
                    outline: none;
                    transition: all 0.3s ease;
                    font-family: 'Montserrat', sans-serif;
                }
                
                .elegant-input:focus, .elegant-select:focus, .elegant-textarea:focus {
                    border-color: #3498DB;
                    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
                }
                
                .elegant-textarea {
                    height: 100px;
                    resize: vertical;
                }
                
                .input-filled {
                    border-color: #3498DB;
                    background-color: #F8FBFD;
                }
                
                .input-error {
                    border-color: #E74C3C;
                    background-color: #FDEDEC;
                }
                
                .input-animated {
                    animation: wiggle 0.5s ease;
                }
                
                .error-message {
                    color: #E74C3C;
                    font-size: 12px;
                    margin-top: 5px;
                    margin-bottom: 0;
                }
                
                .row-group {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                }
                
                .column-group {
                    flex: 1;
                    min-width: 200px;
                }
                
                /* RadioGroup styling */
                .elegant-radio-group {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 10px;
                }
                
                .elegant-radio {
                    padding: 10px 20px;
                    border-radius: 6px;
                    border: 1px solid #dfe4ea;
                    color: #2D3436;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                }
                
                .elegant-radio-active {
                    background-color: #EBF5FB;
                    color: #3498DB;
                    border-color: #3498DB;
                    transform: translateY(-2px);
                    box-shadow: 0 2px 6px rgba(52, 152, 219, 0.15);
                }
                
                /* Hotel field styling */
                .hotel-input-group {
                    position: relative;
                }
                
                .hotel-autocomplete-container {
                    position: relative;
                }
                
                .hotel-suggestions {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #dfe4ea;
                    border-radius: 0 0 6px 6px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
                    z-index: 10;
                    max-height: 200px;
                    overflow-y: auto;
                }
                
                .hotel-suggestion-item {
                    padding: 10px 15px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    transition: background-color 0.2s;
                }
                
                .hotel-suggestion-item:hover {
                    background-color: #f8f9fa;
                }
                
                .hotel-icon {
                    margin-right: 10px;
                    font-size: 18px;
                }
                
                /* Date selection styling */
                .date-selection-container {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                
                .date-column {
                    flex: 1;
                    min-width: 200px;
                }
                
                .date-picker-wrapper {
                    position: relative;
                }
                
                .date-input {
                    width: 91%;
                    padding: 12px 15px;
                    // padding-right: 40px;
                    border: 1px solid #dfe4ea;
                    border-radius: 6px;
                    font-size: 15px;
                    outline: none;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .date-icon {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    pointer-events: none;
                }
                
                .stay-duration {
                    width: 91%;
                    background-color: #f8f9fa;
                    padding: 10px 15px;
                    border-radius: 6px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 10px;
                    margin-top: 10px;
                }
                
                .duration-label {
                    color: #7F8C8D;
                    font-size: 14px;
                }
                
                .duration-value {
                    font-weight: bold;
                    color: #2C3E50;
                    font-size: 16px;
                }
                
                /* Time selection styling */
                .time-selection-section {
                    margin-top: 30px;
                }
                
                .time-section-title {
                    font-size: 18px;
                    color: #2C3E50;
                    margin-bottom: 5px;
                }
                
                .time-section-desc {
                    font-size: 14px;
                    color: #7F8C8D;
                    margin-bottom: 20px;
                }
                
                .time-selection-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                
                .time-picker-container {
                    flex: 1;
                    min-width: 230px;
                }
                
                .time-picker-ui {
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    padding: 10px;
                    border: 1px solid #dfe4ea;
                }
                
                .time-presets {
                    display: flex;
                    gap: 5px;
                    margin-bottom: 10px;
                }
                
                .time-preset-btn {
                    flex: 1;
                    padding: 8px;
                    background: white;
                    border: 1px solid #dfe4ea;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 13px;
                    text-align: center;
                }
                
                .time-preset-btn.selected {
                    background-color: #EBF5FB;
                    color: #3498DB;
                    border-color: #3498DB;
                    font-weight: bold;
                }
                
                .time-input-wrapper {
                    position: relative;
                }
                
                .time-input {
                    width: 91%;
                    padding: 12px 15px;
                    // padding-right: 40px;
                    border: 1px solid #dfe4ea;
                    border-radius: 6px;
                    font-size: 15px;
                    outline: none;
                    transition: all 0.3s ease;
                }
                
                .time-indicator {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 16px;
                }
                
                .time-icon {
                    font-size: 12px;
                    margin-left: 3px;
                }
                
                /* Sleep duration indicator */
                .sleep-duration-indicator {
                    background-color: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                    margin: 15px 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                
                .sleep-duration-label {
                    font-size: 14px;
                    color: #7F8C8D;
                }
                
                .sleep-duration-value {
                    font-size: 24px;
                    font-weight: bold;
                    color: #2C3E50;
                    margin: 5px 0;
                }
                
                .sleep-quality-indicator {
                    font-size: 13px;
                    color: #7F8C8D;
                }
                
                /* Sleep cycle visualization */
                .sleep-cycle-visualization {
                    margin: 30px 0;
                    text-align: center;
                }
                
                .visualization-title {
                    font-size: 16px;
                    color: #2C3E50;
                    margin-bottom: 5px;
                }
                
                .visualization-description {
                    font-size: 14px;
                    color: #7F8C8D;
                    margin-bottom: 20px;
                }
                
                .circle-clock {
                    width: 220px;
                    height: 220px;
                    border-radius: 50%;
                    border: 2px solid #dfe4ea;
                    position: relative;
                    margin: 0 auto;
                }
                
                .clock-center {
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background-color: #3498DB;
                    border-radius: 50%;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }
                
                .hour-markers {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }
                
                .hour-marker {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    text-align: center;
                }
                
                .hour-label {
                    display: inline-block;
                    position: absolute;
                    top: 15px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 10px;
                    color: #7F8C8D;
                }
                
                .day-night-indicator {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }
                
                .day-arc {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                    border-radius: 50%;
                    border: 6px solid transparent;
                    border-top: 6px solid rgba(255, 193, 7, 0.2);
                    border-right: 6px solid rgba(255, 193, 7, 0.2);
                    transform: rotate(-90deg);
                }
                
                .night-arc {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                    border-radius: 50%;
                    border: 6px solid transparent;
                    border-bottom: 6px solid rgba(52, 73, 94, 0.2);
                    border-left: 6px solid rgba(52, 73, 94, 0.2);
                    transform: rotate(-90deg);
                }
                
                .sun-icon {
                    position: absolute;
                    top: 10px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 16px;
                }
                
                .moon-icon {
                    position: absolute;
                    bottom: 10px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 16px;
                }
                
                .sleep-arc {
    position: absolute;
    height: 6px; /* Changed from 100% to a fixed border width */
    width: 110px; /* Half of circle width */
    top: 50%;
    left: 50%;
    border: none; /* Remove previous border styling */
    background-color: #3498DB; /* Use background instead of border */
    transform-origin: left center; /* Changed transform origin point */
    transform: rotate(0deg); /* Will be set dynamically */
    z-index: 2;
}
                
                /* Info card */
                .info-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 15px;
                    background-color: #EBF5FB;
                    padding: 15px;
                    border-radius: 8px;
                    margin-top: 30px;
                }
                
                .info-icon {
                    font-size: 24px;
                    flex-shrink: 0;
                }
                
                .info-content {
                    flex: 1;
                }
                
                .info-title {
                    font-size: 16px;
                    color: #2C3E50;
                    margin: 0 0 5px 0;
                }
                
                .info-text {
                    font-size: 14px;
                    color: #34495E;
                    margin: 0;
                    line-height: 1.5;
                }
                
                /* Intensity selector */
                .intensity-selector {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    padding: 5px;
                    border: 1px solid #dfe4ea;
                    position: relative;
                }
                
                .intensity-slider {
                    position: absolute;
                    height: calc(100% - 10px);
                    background-color: #3498DB;
                    top: 5px;
                    border-radius: 5px;
                    transition: left 0.3s ease;
                    z-index: 0;
                }
                
                .intensity-option {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 12px 8px;
                    cursor: pointer;
                    border-radius: 5px;
                    transition: all 0.3s ease;
                    position: relative;
                    z-index: 1;
                }
                
                .intensity-option-active {
                    color: white;
                }
                
                .intensity-value {
                    font-size: 18px;
                    font-weight: bold;
                }
                
                .intensity-label {
                    font-size: 11px;
                }
                
                /* Review step */
                .summary-section {
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                
                .summary-title {
                    font-size: 18px;
                    color: #2C3E50;
                    margin-top: 0;
                    margin-bottom: 15px;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 15px;
                }
                
                .summary-item {
                    font-size: 14px;
                }
                
                .summary-label {
                    font-weight: 600;
                    color: #7F8C8D;
                    margin-right: 5px;
                }
                
                .summary-value {
                    color: #2C3E50;
                }
                
                /* Terms and congrats */
                .terms-container {
                    display: flex;
                    align-items: flex-start;
                    margin: 20px 0;
                }
                
                .terms-checkbox {
                    width: 20px;
                    height: 20px;
                    margin-right: 10px;
                    margin-top: 3px;
                }
                
                .terms-label {
                    font-size: 14px;
                    color: #34495E;
                    line-height: 1.6;
                }
                
                .terms-link {
                    color: #3498DB;
                    text-decoration: underline;
                }
                
                .congrats-message {
                    background-color: #EAF2F8;
                    border-radius: 8px;
                    padding: 20px;
                    text-align: center;
                    margin: 30px 0 20px;
                }
                
                .congrats-icon {
                    font-size: 32px;
                    margin-bottom: 10px;
                    animation: pulse 2s infinite;
                }
                
                .congrats-title {
                    font-size: 18px;
                    font-weight: bold;
                    color: #2980B9;
                    margin-bottom: 5px;
                }
                
                .congrats-text {
                    font-size: 15px;
                    color: #34495E;
                    line-height: 1.5;
                }
                
                /* Navigation buttons */
                .navigation-buttons {
                    display: flex;
                    justify-content: space-between;
                    padding: 0 30px 30px;
                }
                
                .prev-button, .next-button, .submit-button {
                    padding: 12px 25px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: none;
                    font-size: 16px;
                }
                
                .prev-button {
                    background-color: #ECF0F1;
                    color: #2C3E50;
                }
                
                .next-button, .submit-button {
                    background-color: #3498DB;
                    color: white;
                    position: relative;
                    overflow: hidden;
                }
                
                .next-button:hover, .submit-button:hover {
                    background-color: #2980B9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
                }
                
                .prev-button:hover {
                    background-color: #E0E7EB;
                    transform: translateY(-2px);
                }
                
                .action-button {
                    background-color: #3498DB;
                    color: white;
                    padding: 15px 30px;
                    border: none;
                    border-radius: 6px;
                    font-size: 18px;
                    font-weight: 600;
                    cursor: pointer;
                    margin-top: 20px;
                    transition: all 0.3s ease;
                }
                
                .action-button:hover {
                    background-color: #2980B9;
                    transform: translateY(-3px);
                    box-shadow: 0 6px 12px rgba(52, 152, 219, 0.3);
                }
                
                .pulse-animation {
                    animation: pulse 2s infinite;
                }
                
                /* Loading animation */
                .loading-dots:after {
                    content: ".";
                    animation: loadingDots 1.5s infinite steps(4);
                }
                
                /* Field description */
                .field-description {
                    font-size: 14px;
                    color: #7F8C8D;
                    margin-top: -5px;
                    margin-bottom: 10px;
                }
                
                /* Slide in animation */
                .slide-in {
                    animation: slideIn 0.5s ease;
                }

                /* Success Page Styles */
                .success-page {
                    padding: 40px;
                    text-align: center;
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.5s ease, transform 0.5s ease;
                    background-color: white;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
                    max-width: 600px;
                    width: 100%;
                }
                
                .fade-in {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                .success-icon {
                    font-size: 60px;
                    margin-bottom: 20px;
                    animation: pulse 2s infinite;
                }
                
                .success-heading {
                    font-size: 28px;
                    color: #2C3E50;
                    margin-bottom: 15px;
                }
                
                .success-text {
                    font-size: 16px;
                    color: #7F8C8D;
                    margin-bottom: 20px;
                    line-height: 1.6;
                }
                
                .success-input {
                    width: 100%;
                    max-width: 300px;
                    padding: 15px;
                    border: 1px solid #dfe4ea;
                    border-radius: 6px;
                    font-size: 16px;
                    margin: 20px auto;
                    text-align: center;
                }
                
                .button-group {
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                    margin-top: 20px;
                }
                
                .confirm-button, .cancel-button {
                    padding: 12px 25px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: none;
                    font-size: 16px;
                }
                
                .confirm-button {
                    background-color: #3498DB;
                    color: white;
                }
                
                .cancel-button {
                    background-color: #ECF0F1;
                    color: #2C3E50;
                }
                
                .disabled-button {
                    background-color: #BDC3C7;
                    cursor: not-allowed;
                    opacity: 0.7;
                }
                
                /* Loading Page Styles */
                .loading-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 50vh;
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.5s ease, transform 0.5s ease;
                }
                
                .loading-card {
                    background-color: white;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
                    padding: 40px;
                    text-align: center;
                    max-width: 600px;
                    width: 100%;
                }
                
                .loading-spinner {
                    width: 60px;
                    height: 60px;
                    border: 6px solid #ECF0F1;
                    border-top: 6px solid #3498DB;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 20px;
                }
                
                .loading-heading {
                    font-size: 24px;
                    color: #2C3E50;
                    margin-bottom: 15px;
                }
                
                .loading-text {
                    font-size: 16px;
                    color: #7F8C8D;
                    margin-bottom: 10px;
                }
                
                .loading-progress-bar {
                    width: 100%;
                    height: 6px;
                    background-color: #ECF0F1;
                    border-radius: 3px;
                    overflow: hidden;
                    margin-top: 30px;
                }
                
                .loading-progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #3498DB, #9b59b6);
                    border-radius: 3px;
                    width: 0;
                    transition: width 0.3s ease;
                }
                
                .completion-message, .error-message {
                    opacity: 0;
                    transform: translateY(20px);
                    animation: fadeIn 0.5s ease forwards;
                }
                
                .completion-heading {
                    font-size: 24px;
                    color: #2C3E50;
                    margin-bottom: 15px;
                }
                
                .completion-text {
                    font-size: 16px;
                    color: #7F8C8D;
                    margin-bottom: 15px;
                }
                
                .countdown-text {
                    font-size: 18px;
                    color: #2C3E50;
                }
                
                .countdown-number {
                    font-weight: bold;
                    color: #3498DB;
                }
                
                .error-icon {
                    font-size: 36px;
                    color: #E74C3C;
                    margin-bottom: 15px;
                }
                
                .retry-button {
                    background-color: #3498DB;
                    color: white;
                    padding: 10px 20px;
                    border: none;
                    border-radius: 6px;
                    font-size: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    margin-top: 15px;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .content-container {
                        padding: 30px;
                    }

                    .duolingo-style-container {
                    padding: 0px;
                }
                    
                    .navigation-buttons {
                        padding: 0 20px 20px;
                    }
                    
                    .elegant-radio-group {
                        flex-direction: column;
                        gap: 10px;
                    }
                    
                    .date-selection-container, 
                    .time-selection-row {
                        flex-direction: column;
                        gap: 15px;
                    }
                    
                    .summary-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .circle-clock {
                        width: 180px;
                        height: 180px;
                    }
                    
                    .time-presets {
                        flex-wrap: wrap;
                    }
                    
                    .prev-button, .next-button, .submit-button {
                        padding: 10px 15px;
                        font-size: 14px;
                    }
                    
                    .success-page, .loading-card {
                        padding: 20px;
                    }
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default WellnessCheckInForm;